import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Eye, EyeOff, X, Edit, Share2, Heart, MessageSquare, Copy } from 'lucide-react';

export default function AdminBlog() {
  const { apiClient } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', category: '', image_url: '', published: true });

  const fetchPosts = useCallback(async () => {
    try { const { data } = await apiClient.get('/api/admin/blog'); setPosts(data); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  }, [apiClient]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const resetForm = () => { setForm({ title: '', excerpt: '', content: '', category: '', image_url: '', published: true }); setEditingId(null); setShowForm(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await apiClient.patch(`/api/admin/blog/${editingId}`, form);
        toast.success('Post updated!');
      } else {
        await apiClient.post('/api/admin/blog', form);
        toast.success('Post created!');
      }
      resetForm();
      fetchPosts();
    } catch { toast.error('Failed'); }
  };

  const handleEdit = (post) => {
    setForm({ title: post.title, excerpt: post.excerpt, content: post.content, category: post.category, image_url: post.image_url || '', published: post.published });
    setEditingId(post.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try { await apiClient.delete(`/api/admin/blog/${id}`); toast.success('Deleted'); fetchPosts(); }
    catch { toast.error('Failed'); }
  };

  const togglePublish = async (id, current) => {
    try { await apiClient.patch(`/api/admin/blog/${id}`, { published: !current }); toast.success(current ? 'Unpublished' : 'Published'); fetchPosts(); }
    catch { toast.error('Failed'); }
  };

  const copyShareUrl = (postId) => {
    const url = `${window.location.origin}/blog?post=${postId}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Share URL copied!')).catch(() => toast.error('Failed to copy'));
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20";

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-2xl font-heading font-bold text-white">Blog Posts</h1><p className="text-white/50 text-sm">{posts.length} articles</p></div>
        <button onClick={() => { if (showForm) resetForm(); else setShowForm(true); }} className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2" data-testid="add-blog-btn">{showForm ? <X size={16} /> : <Plus size={16} />} {showForm ? 'Cancel' : 'New Post'}</button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 p-6 rounded-2xl border border-white/8 bg-white/[0.03] space-y-4" data-testid="blog-form">
          <h3 className="text-white font-heading font-bold">{editingId ? 'Edit Post' : 'Create New Post'}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <input placeholder="Post Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className={inputCls} data-testid="blog-title-input" />
            <input placeholder="Category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} required className={inputCls} data-testid="blog-category-input" />
          </div>
          <input placeholder="Image URL (optional)" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className={inputCls} />
          <textarea placeholder="Short excerpt" value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} required rows="2" className={inputCls + " resize-none"} data-testid="blog-excerpt-input" />
          <textarea placeholder="Full content (## headings, - lists)" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} required rows="8" className={inputCls + " resize-none font-mono text-xs"} data-testid="blog-content-input" />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-white/60 text-sm cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 rounded" />
              {editingId ? 'Published' : 'Publish immediately'}
            </label>
            <button type="submit" className="bg-gradient-to-r from-emerald-500 to-emerald-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold" data-testid="blog-submit-btn">{editingId ? 'Update Post' : 'Create Post'}</button>
          </div>
        </form>
      )}

      {loading ? <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/40" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {posts.map(p => (
            <div key={p.id} className="rounded-2xl border border-white/8 bg-white/[0.03] overflow-hidden group" data-testid={`admin-blog-${p.id}`}>
              <div className="h-36 overflow-hidden relative">
                {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-blue-500/20 to-violet-500/20" />}
                <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${p.published ? 'bg-green-500/80 text-white' : 'bg-yellow-500/80 text-white'}`}>{p.published ? 'Live' : 'Draft'}</span>
              </div>
              <div className="p-4">
                <span className="px-2 py-0.5 rounded bg-blue-500/15 text-blue-300 text-[10px] font-bold">{p.category}</span>
                <h3 className="text-white font-heading font-bold text-sm mt-2 line-clamp-2">{p.title}</h3>
                <p className="text-white/40 text-xs mt-1 line-clamp-2">{p.excerpt}</p>
                <div className="flex items-center gap-3 mt-3 text-white/30 text-xs">
                  <span className="flex items-center gap-1"><Heart size={10} /> {p.likes || 0}</span>
                  <span className="flex items-center gap-1"><MessageSquare size={10} /> {p.comments?.length || 0}</span>
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-1.5 mt-3 pt-3 border-t border-white/5">
                  <button onClick={() => handleEdit(p)} className="p-1.5 rounded-lg bg-white/5 text-white/40 hover:text-blue-300 transition-colors" title="Edit" data-testid={`edit-blog-${p.id}`}><Edit size={13} /></button>
                  <button onClick={() => togglePublish(p.id, p.published)} className="p-1.5 rounded-lg bg-white/5 text-white/40 hover:text-yellow-300 transition-colors" title={p.published ? 'Unpublish' : 'Publish'}>{p.published ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                  <button onClick={() => copyShareUrl(p.id)} className="p-1.5 rounded-lg bg-white/5 text-white/40 hover:text-green-300 transition-colors" title="Copy Share URL" data-testid={`share-blog-${p.id}`}><Share2 size={13} /></button>
                  <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded-lg bg-white/5 text-white/40 hover:text-red-400 transition-colors ml-auto" title="Delete" data-testid={`delete-blog-${p.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
